import React from "react";
//#region redux
import { useNavigate } from "react-router-dom";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { AllCompaniesEnums } from "models/enums";
import { CompanyTodoCardData } from "models/propTypes/allCompaniesPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  BreadCrumb,
  DashboardButtonGroup,
  DashboardTitle,
  LoadingWithLogo,
  PageLayouts,
  ReportSearchInput,
  SearchContextProvider,
  ToggleButtonGroup,
  useSearchContext,
} from "components/common";
import CompanyCard from "./companyCard";
import RefreshStatus from "./refreshStatus";
import useAllCompaniesTodos from "./useAllCompaniesTodos";
import { useAllCompaniesTodoSetting } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const byName = (first: CompanyTodoCardData, second: CompanyTodoCardData) =>
  first.companyName.localeCompare(second.companyName, "sv");

const sortCards = (cards: CompanyTodoCardData[], sortOrder: AllCompaniesEnums.TodoOverviewSortOrder) => {
  switch (sortOrder) {
    case AllCompaniesEnums.TodoOverviewSortOrder.TodoCount:
      return [...cards].sort((first, second) => second.total - first.total || byName(first, second));
    case AllCompaniesEnums.TodoOverviewSortOrder.Overdue:
      return [...cards].sort(
        (first, second) => second.overdue - first.overdue || second.total - first.total || byName(first, second)
      );
    default:
      return [...cards].sort(byName);
  }
};

const matchesSearch = (card: CompanyTodoCardData, searchText: string) =>
  searchText === "" ||
  card.companyName.toLowerCase().includes(searchText) ||
  (card.companyOrg ?? "").toLowerCase().includes(searchText);

const sortOptions = [
  { sortOrder: AllCompaniesEnums.TodoOverviewSortOrder.Name, label: TranslationKeys.Todos.AllCompaniesSortName },
  { sortOrder: AllCompaniesEnums.TodoOverviewSortOrder.TodoCount, label: TranslationKeys.Todos.AllCompaniesSortTodoCount },
  { sortOrder: AllCompaniesEnums.TodoOverviewSortOrder.Overdue, label: TranslationKeys.Todos.AllCompaniesSortOverdue },
];

/** The page body, split from the view so it can read the search context the view provides. */
const AllCompaniesContent: React.FC = () => {
  const navigate = useNavigate();
  const searchContext = useSearchContext();

  const { cards, refreshing, refreshedAt, reload } = useAllCompaniesTodos();
  const [sortOrder, setSortOrder] = React.useState(AllCompaniesEnums.TodoOverviewSortOrder.Name);

  const searchText = searchContext?.searchText?.trim().toLowerCase() ?? "";

  const visibleCards = React.useMemo(
    () => sortCards(cards.filter((card) => matchesSearch(card, searchText)), sortOrder),
    [cards, searchText, sortOrder]
  );

  const openCompany = (card: CompanyTodoCardData) => {
    if (card.companyUrl) navigate(`/${card.companyUrl}`);
  };

  const sortButtons = sortOptions.map((option) => ({
    buttonKey: option.sortOrder,
    children: LocalizationHelperInstance.getFormattedMessage(option.label),
    onClick: () => setSortOrder(option.sortOrder),
  }));

  return (
    <Grid container direction="column" wrap="nowrap" className={cssExports.allCompaniesTodoOverview}>
      <Grid
        container
        item
        wrap="nowrap"
        alignItems="flex-start"
        justifyContent="space-between"
        className={cssExports.allCompaniesTodoOverviewHeader}
      >
        <Grid
          item
          container
          direction="column"
          alignItems="flex-start"
          xs
          className={cssExports.allCompaniesTodoOverviewTitle}
        >
          <Grid item>
            <DashboardTitle title={TranslationKeys.Todos.AllCompanies} />
          </Grid>
          <ToggleButtonGroup colorVariation="dark" initialSelectionKey={sortOrder} buttons={sortButtons} />
        </Grid>
        <Grid item container direction="column" alignItems="flex-end" width="auto">
          <DashboardButtonGroup
            buttons={[
              <ReportSearchInput
                key="search"
                placeholder={LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Todos.AllCompaniesSearchCompany
                )}
              />,
            ]}
          />
          {/* No automatic refresh: every reload runs the full To-do pipeline once per company in Core. */}
          <RefreshStatus refreshedAt={refreshedAt} refreshing={refreshing} onRefresh={() => void reload()} />
        </Grid>
      </Grid>
      <Grid item>
        {visibleCards.length === 0 && cards.length > 0 ? (
          <div className={cssExports.allCompaniesTodoOverviewEmpty}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompaniesNoMatch)}
          </div>
        ) : (
          <div className={cssExports.allCompaniesTodoOverviewGrid}>
            {visibleCards.map((card) => (
              <CompanyCard key={card.companyId} card={card} onClick={openCompany} />
            ))}
          </div>
        )}
      </Grid>
    </Grid>
  );
};

/**
 * Cross-company To-do overview: one card per company the user has access to, with its To-do count.
 * A card opens that company's dashboard; the slug in the url switches the active company.
 */
const AllCompaniesView: React.FC = () => {
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompanies);
  const { showOverview } = useAllCompaniesTodoSetting();

  // Off shows the no-permission view and fetches nothing; unknown waits rather than flashing it.
  return (
    <SearchContextProvider>
      <PageLayouts.EntityFullScreen
        entityKey="allCompaniesTodoOverview"
        id={0}
        hasPermissions={showOverview !== false}
        navigation={<BreadCrumb prevItems={[]} current={title} showHome />}
      >
        {showOverview ? <AllCompaniesContent /> : <LoadingWithLogo />}
      </PageLayouts.EntityFullScreen>
    </SearchContextProvider>
  );
};

export default AllCompaniesView;
